import React, { useMemo, useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { Check } from '@mui/icons-material'
import { UserAvatar } from '../util/avatars'
import { useBrand } from './ui'
import { stepChannelsApi, useNotifyOrder } from './api'
import { orderSteps, orderText } from './BotAdmin'
import AppLink from '../util/AppLink'
import { TeamHeading, fromLabel, groupByTeam, teamPhoto } from './teamsGroups'

export { teamPhoto }

// GoAlert's urql client suspends by default; this dialog renders its own states.
const noSuspense = { suspense: false }

const optionsQuery = gql`
  query ErecordsStepOptions {
    users(input: { first: 100 }) {
      nodes {
        id
        name
      }
    }
    schedules(input: { first: 100 }) {
      nodes {
        id
        name
      }
    }
    teams: destinationFieldSearch(
      input: {
        destType: "erecords-teams-workflow"
        fieldID: "teams_channel"
        first: 100
      }
    ) {
      nodes {
        value
        label
      }
    }
  }
`
const createStep = gql`
  mutation ErecordsCreateStep($input: CreateEscalationPolicyStepInput!) {
    createEscalationPolicyStep(input: $input) {
      id
    }
  }
`
const updateStep = gql`
  mutation ErecordsUpdateStep($input: UpdateEscalationPolicyStepInput!) {
    updateEscalationPolicyStep(input: $input)
  }
`

export type StepAction = {
  type: string
  args: Record<string, string>
  displayInfo?: { text?: string }
}

const USER = 'builtin-user'
const SCHEDULE = 'builtin-schedule'
const TEAMS = 'erecords-teams-workflow'
const waits = [5, 10, 15, 30, 60]

// StepDialog adds or edits one escalation step: tick people, schedules and
// Teams channels, choose how long to wait. Other destinations already on the
// step (webhooks, …) are kept and can be removed.
export default function StepDialog(props: {
  policyID: string
  stepID?: string // edit when set
  actions?: StepAction[]
  delayMinutes?: number
  channels?: string[] // "what to send" for this step; none = everything
  stepNumber?: number
  last?: boolean // the policy's last step: its wait only matters on repeat
  repeat?: number
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const order = useNotifyOrder()
  const [tab, setTab] = useState(0)
  const [q] = useQuery({ query: optionsQuery, context: noSuspense })
  const [cm, commitCreate] = useMutation(createStep)
  const [um, commitUpdate] = useMutation(updateStep)
  const initial = props.actions ?? []
  const pick = (type: string, key: string): string[] =>
    initial.filter((a) => a.type === type).map((a) => a.args[key])
  const [users, setUsers] = useState<string[]>(pick(USER, 'user_id'))
  const [schedules, setSchedules] = useState<string[]>(
    pick(SCHEDULE, 'schedule_id'),
  )
  const [teams, setTeams] = useState<string[]>(pick(TEAMS, 'teams_channel'))
  const [others, setOthers] = useState<StepAction[]>(
    initial.filter((a) => ![USER, SCHEDULE, TEAMS].includes(a.type)),
  )
  const [delay, setDelay] = useState(props.delayMinutes ?? 15)
  const ways = orderSteps.filter((w) => order?.teams || !w.teams)
  // none picked: each person's notification order
  const [channels, setChannels] = useState<string[]>(props.channels ?? [])
  const [chErr, setChErr] = useState('')
  const [custom, setCustom] = useState(!waits.includes(delay))
  const [search, setSearch] = useState('')

  const toggle = (
    list: string[],
    set: (v: string[]) => void,
    id: string,
  ): void =>
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id])
  const match = (s: string): boolean =>
    s.toLowerCase().includes(search.toLowerCase())

  const allUsers: { id: string; name: string }[] = q.data?.users?.nodes ?? []
  const allSchedules: { id: string; name: string }[] =
    q.data?.schedules?.nodes ?? []
  const allTeams: { value: string; label: string }[] =
    q.data?.teams?.nodes ?? []

  const actions = useMemo(
    () => [
      ...users.map((id) => ({ type: USER, args: { user_id: id } })),
      ...schedules.map((id) => ({ type: SCHEDULE, args: { schedule_id: id } })),
      ...teams.map((id) => ({ type: TEAMS, args: { teams_channel: id } })),
      ...others.map((a) => ({ type: a.type, args: a.args })),
    ],
    [users, schedules, teams, others],
  )
  const count = actions.length
  const error = cm.error || um.error
  const busy = cm.fetching || um.fetching

  const save = (): void => {
    const done = (r: {
      error?: unknown
      data?: { createEscalationPolicyStep?: { id: string } }
    }): void => {
      if (r.error) return
      const id = props.stepID ?? r.data?.createEscalationPolicyStep?.id
      if (!id) {
        props.onClose()
        return
      }
      stepChannelsApi
        .set(id, channels)
        .then(props.onClose)
        .catch((e: Error) => setChErr(e.message))
    }
    if (props.stepID) {
      commitUpdate(
        { input: { id: props.stepID, delayMinutes: delay, actions } },
        { additionalTypenames: ['EscalationPolicy'] },
      ).then(done)
    } else {
      commitCreate(
        {
          input: {
            escalationPolicyID: props.policyID,
            delayMinutes: delay,
            actions,
          },
        },
        { additionalTypenames: ['EscalationPolicy'] },
      ).then(done)
    }
  }

  const list = (
    items: { id: string; name: string }[],
    selected: string[],
    set: (v: string[]) => void,
    empty: string,
    avatar?: boolean,
    wide?: boolean,
  ): React.JSX.Element => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: wide
          ? '1fr'
          : 'repeat(auto-fill, minmax(170px, 1fr))',
        gap: 1,
        maxHeight: 260,
        overflowY: 'auto',
        py: 1,
      }}
    >
      {!q.data && (
        <Typography sx={{ color: c.muted, fontSize: 14 }}>Loading…</Typography>
      )}
      {q.data && items.filter((i) => match(i.name)).length === 0 && (
        <Typography sx={{ color: c.muted, fontSize: 14 }}>{empty}</Typography>
      )}
      {items
        .filter((i) => match(i.name))
        .map((i) => {
          const sel = selected.includes(i.id)
          return (
            <Box
              key={i.id}
              role='checkbox'
              aria-checked={sel}
              tabIndex={0}
              onClick={() => toggle(selected, set, i.id)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') &&
                toggle(selected, set, i.id)
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: '12px',
                cursor: 'pointer',
                border: `1.5px solid ${sel ? c.ink : c.line}`,
                bgcolor: sel ? c.soft : 'transparent',
                '&:hover': { bgcolor: c.hover },
              }}
            >
              {avatar ? (
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <UserAvatar userID={i.id} sx={{ width: 30, height: 30 }} />
                  {sel && (
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        right: -3,
                        bottom: -3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: c.ink,
                        color: c.surface,
                        border: `2px solid ${c.surface}`,
                        '& svg': { fontSize: 10 },
                      }}
                    >
                      <Check />
                    </Box>
                  )}
                </Box>
              ) : wide ? (
                <Avatar
                  variant='rounded'
                  src={teamPhoto(i.id)}
                  alt=''
                  sx={{ width: 30, height: 30 }}
                />
              ) : (
                <Checkbox
                  size='small'
                  checked={sel}
                  sx={{ p: 0 }}
                  tabIndex={-1}
                />
              )}
              <Typography
                sx={{ fontSize: 14, fontWeight: sel ? 600 : 400 }}
                noWrap
                title={i.name}
              >
                {i.name}
              </Typography>
            </Box>
          )
        })}
    </Box>
  )

  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {props.stepID ? 'Edit step' : 'Add step'}
        {props.stepNumber ? ` ${props.stepNumber}` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontWeight: 600, mb: 0.75 }}>
          Who to notify
        </Typography>
        {/* everything picked, across the three tabs */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            minHeight: 40,
            alignItems: 'center',
            p: 0.75,
            mb: 1,
            borderRadius: '12px',
            border: `1px dashed ${c.line}`,
          }}
        >
          {count === 0 && (
            <Typography sx={{ fontSize: 13, color: c.muted, px: 0.75 }}>
              Nobody yet. Pick people, schedules or Teams channels below.
            </Typography>
          )}
          {users.map((id) => (
            <Chip
              key={'u' + id}
              size='small'
              avatar={<UserAvatar userID={id} />}
              label={allUsers.find((u) => u.id === id)?.name ?? 'Someone'}
              onDelete={() => setUsers(users.filter((x) => x !== id))}
            />
          ))}
          {schedules.map((id) => (
            <Chip
              key={'s' + id}
              size='small'
              label={`${allSchedules.find((x) => x.id === id)?.name ?? 'Schedule'} · on call`}
              onDelete={() => setSchedules(schedules.filter((x) => x !== id))}
            />
          ))}
          {teams.map((id) => (
            <Chip
              key={'t' + id}
              size='small'
              avatar={<Avatar variant='rounded' src={teamPhoto(id)} alt='' />}
              label={
                allTeams.find((x) => x.value === id)?.label ?? 'Teams channel'
              }
              onDelete={() => setTeams(teams.filter((x) => x !== id))}
            />
          ))}
          {others.map((a, i) => (
            <Chip
              key={'o' + i}
              size='small'
              label={a.displayInfo?.text || a.type}
              onDelete={() => setOthers(others.filter((_, k) => k !== i))}
            />
          ))}
        </Box>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, textTransform: 'none' },
          }}
        >
          <Tab label={`People${users.length ? ` · ${users.length}` : ''}`} />
          <Tab
            label={`Schedules${schedules.length ? ` · ${schedules.length}` : ''}`}
          />
          <Tab
            label={`Teams channels${teams.length ? ` · ${teams.length}` : ''}`}
          />
        </Tabs>
        <TextField
          size='small'
          fullWidth
          placeholder='Search'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 1 }}
        />
        {tab === 0 && list(allUsers, users, setUsers, 'No people found.', true)}
        {tab === 1 &&
          list(
            allSchedules,
            schedules,
            setSchedules,
            'No schedules yet. Whoever a schedule says is on call gets notified.',
          )}
        {tab === 2 && (
          <Box sx={{ maxHeight: 300, overflowY: 'auto', py: 1 }}>
            {!q.data && (
              <Typography sx={{ color: c.muted, fontSize: 14 }}>
                Loading…
              </Typography>
            )}
            {q.data && allTeams.length === 0 && (
              <Typography sx={{ color: c.muted, fontSize: 14 }}>
                No Teams channels found (Admin &gt; Microsoft Teams).
              </Typography>
            )}
            {groupByTeam(allTeams.map((t) => fromLabel(t.value, t.label)))
              .map((g) => ({
                ...g,
                channels: g.channels.filter((ch) =>
                  match(g.team + ' ' + ch.channel),
                ),
              }))
              .filter((g) => g.channels.length)
              .map((g) => (
                <Box key={g.teamID} sx={{ mb: 1.5 }}>
                  <TeamHeading team={g.team} channel={g.channels[0].value} />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(190px, 1fr))',
                      gap: 0.75,
                      mt: 0.75,
                      pl: 3.5,
                    }}
                  >
                    {g.channels.map((ch) => {
                      const sel = teams.includes(ch.value)
                      return (
                        <Box
                          key={ch.value}
                          role='checkbox'
                          aria-checked={sel}
                          tabIndex={0}
                          onClick={() => toggle(teams, setTeams, ch.value)}
                          onKeyDown={(e) =>
                            (e.key === 'Enter' || e.key === ' ') &&
                            toggle(teams, setTeams, ch.value)
                          }
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1,
                            py: 0.75,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            border: `1.5px solid ${sel ? c.ink : c.line}`,
                            bgcolor: sel ? c.soft : 'transparent',
                            '&:hover': { bgcolor: c.hover },
                          }}
                        >
                          <Checkbox
                            size='small'
                            checked={sel}
                            sx={{ p: 0 }}
                            tabIndex={-1}
                          />
                          <Typography
                            sx={{ fontSize: 14, fontWeight: sel ? 600 : 400 }}
                            noWrap
                            title={ch.channel}
                          >
                            {ch.channel}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              ))}
          </Box>
        )}

        {order && users.length + schedules.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              What to send
            </Typography>
            <Typography sx={{ fontSize: 13, color: c.muted, mb: 1 }}>
              {channels.length ? (
                'Sent to everyone on this step as soon as it starts. The wait below decides when the next step starts.'
              ) : (
                <React.Fragment>
                  Each person&apos;s notification order:{' '}
                  {orderText(order.delays, order.teams)}.{' '}
                  <Link
                    component={AppLink}
                    to='/admin/teams'
                    sx={{ color: c.ink }}
                  >
                    Change the order
                  </Link>
                </React.Fragment>
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={channels.length ? undefined : <Check />}
                label='Notification order'
                color={channels.length ? 'default' : 'primary'}
                variant={channels.length ? 'outlined' : 'filled'}
                onClick={() => setChannels([])}
                sx={{ ml: '0 !important' }}
              />
              {ways.map((w) => {
                const on = channels.includes(w.key)
                return (
                  <Chip
                    key={w.key}
                    icon={on ? <Check /> : undefined}
                    label={w.label}
                    color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'}
                    onClick={() =>
                      setChannels(
                        on
                          ? channels.filter((k) => k !== w.key)
                          : [...channels, w.key],
                      )
                    }
                    sx={{ ml: '0 !important' }}
                  />
                )
              })}
            </Box>
          </Box>
        )}

        <Typography sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>
          Wait for an acknowledgement
        </Typography>
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          {waits.map((w) => {
            const on = !custom && delay === w
            return (
              <Chip
                key={w}
                label={`${w} min`}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
                onClick={() => {
                  setCustom(false)
                  setDelay(w)
                }}
                sx={{ ml: '0 !important' }}
              />
            )
          })}
          <Chip
            label='Custom'
            color={custom ? 'primary' : 'default'}
            variant={custom ? 'filled' : 'outlined'}
            onClick={() => setCustom(true)}
            sx={{ ml: '0 !important' }}
          />
          {custom && (
            <TextField
              size='small'
              type='number'
              label='Minutes'
              value={delay}
              onChange={(e) =>
                setDelay(
                  Math.max(1, Math.min(9000, Number(e.target.value) || 1)),
                )
              }
              sx={{ width: 110 }}
              inputProps={{ min: 1, max: 9000 }}
            />
          )}
        </Stack>
        <Typography sx={{ fontSize: 13, color: c.muted, mt: 1 }}>
          {!props.last
            ? `If nobody acknowledges within ${delay} min, the next step starts.`
            : props.repeat
              ? `This is the last step: if nobody acknowledges within ${delay} min, the policy starts over from step 1.`
              : `This is the last step and the policy then stops, so the wait only matters if you later add a step or let it start over.`}
        </Typography>

        {(error || chErr) && (
          <Alert severity='error' sx={{ mt: 1.5 }}>
            {error ? error.message.replace('[GraphQL] ', '') : chErr}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography sx={{ fontSize: 13, color: c.muted, mr: 'auto' }}>
          {count === 0
            ? 'Notifies nobody'
            : `Notifies ${count} · waits ${delay} min`}
        </Typography>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button variant='contained' disabled={busy} onClick={save}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
